import React, { useState } from 'react';

import { WarningOutlined } from '@ant-design/icons';
import { DatePicker, Modal as ModalAntD, message } from 'antd';
import moment, { Moment as MomentType } from 'moment';
import { T, TF } from 'react-translator-component';
import styled from 'styled-components';

import { INTEGRATION_SCOPES, isSensitiveScope, scopeHelp, scopeLabel } from './integrationLabels';
import { tokenExpiryPayload } from './tokenExpiry';

import { createIntegrationToken } from '../../../../hooks/interoperability';
import {
  IntegrationScope,
  IntegrationTokenCreated,
} from '../../../../types/responses/Interoperability';

const Modal: any = ModalAntD;

const Label = styled.label`
  display: block;
  font-size: 13px;
  color: #4a5462;
  margin-bottom: 6px;
`;

const NameInput = styled.input<{ invalid: boolean }>`
  width: 100%;
  border: 1px solid ${({ invalid }) => (invalid ? '#c8352f' : '#d1d5db')};
  border-radius: 3px;
  padding: 8px 10px;
  font-size: 13px;

  &:focus {
    outline: none;
    border-color: ${({ theme }) => theme.primary};
    box-shadow: 0 0 2px ${({ theme }) => theme.primary};
  }
`;

const Optional = styled.span`
  color: #8d929e;
  font-size: 12px;
`;

const Hint = styled.div`
  color: #8d929e;
  font-size: 12px;
  margin-top: 4px;
`;

const Block = styled.div`
  margin-top: 18px;
`;

const ScopeRow = styled.div<{ marked: boolean }>`
  display: flex;
  gap: 10px;
  align-items: flex-start;
  border: 1px solid ${({ marked, theme }) => (marked ? theme.primary : '#e9ebee')};
  border-radius: 3px;
  padding: 10px 12px;
  margin-bottom: 8px;
  cursor: pointer;
`;

const Mark = styled.span<{ marked: boolean }>`
  width: 16px;
  height: 16px;
  min-width: 16px;
  margin-top: 2px;
  border-radius: 3px;
  border: 1.4px solid ${({ marked, theme }) => (marked ? theme.primary : '#c6cbd3')};
  background: ${({ marked, theme }) => (marked ? theme.primary : '#fff')};
  color: #fff;
  font-size: 11px;
  line-height: 14px;
  text-align: center;
`;

const ScopeName = styled.div`
  font-size: 13px;
  color: #12263f;
  display: flex;
  align-items: center;
  gap: 6px;
`;

const ScopeHelp = styled.div`
  font-size: 12px;
  color: #8d929e;
  margin-top: 2px;
  line-height: 1.5;
`;

const Sensitive = styled.span`
  color: #a8500f;
  font-size: 11px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
`;

const Error = styled.div`
  color: #b02525;
  font-size: 12px;
  margin-top: 6px;
`;

const Foot = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const Note = styled.span`
  flex: 1;
  color: #8d929e;
  font-size: 12px;
`;

const CancelButton = styled.button`
  background: #fff;
  color: #4a5462;
  border: 1px solid #d1d5db;
  border-radius: 3px;
  padding: 8px 14px;
  cursor: pointer;
  font-size: 13px;
`;

const CreateButton = styled.button`
  background: ${({ theme }) => theme.primary};
  color: #fff;
  border: none;
  border-radius: 3px;
  padding: 8px 16px;
  cursor: pointer;
  font-size: 13px;

  &:disabled {
    background: #e3e6ea;
    color: #a9b4c2;
    cursor: not-allowed;
  }
`;

export type IntegrationTokenCreateModalProps = {
  tenantId: string;
  onClose: () => void;

  /** Criado: a tela de trás exibe o token uma única vez e recarrega a lista. */
  onCreated: (created: IntegrationTokenCreated) => void;
};

/**
 * Criação de token de integração.
 *
 * O bloco de escopos é a parte com regra: eles são independentes, então a pessoa marca um
 * por um, e os dois que causam dano — operar recarga e gerenciar contrato — vêm por último
 * e marcados como sensíveis. Quem lê de cima para baixo encontra o inofensivo primeiro.
 *
 * Nada é marcado por padrão: credencial nasce sem poder, e quem cria diz o que libera.
 */
export const IntegrationTokenCreateModal: React.FC<IntegrationTokenCreateModalProps> = ({
  tenantId,
  onClose,
  onCreated,
}) => {
  const [name, setName] = useState('');
  const [contactEmail, setContactEmail] = useState('');
  const [scopes, setScopes] = useState<IntegrationScope[]>([]);
  const [expiresAt, setExpiresAt] = useState<MomentType | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const toggle = (scope: IntegrationScope) => {
    setScopes((current) =>
      current.includes(scope)
        ? current.filter((marked) => marked !== scope)
        : [...current, scope],
    );
  };

  const ready = name.trim().length > 0 && scopes.length > 0;

  const create = async () => {
    if (!ready) {
      return;
    }

    try {
      setBusy(true);
      setError('');

      const created = await createIntegrationToken(tenantId, {
        name: name.trim(),
        scopes,
        expires_at: tokenExpiryPayload(expiresAt),
        contact_email: contactEmail.trim() || null,
      });

      onCreated(created);
    } catch (requestError) {
      // A mensagem do servidor é mais precisa que qualquer texto genérico aqui: ela diz
      // qual campo recusou.
      const fromServer = (requestError as { response?: { data?: { message?: string } } })
        ?.response?.data?.message;

      setError(fromServer || TF('Could not create the token now. Try again in a moment.'));
      message.error(T('Could not create the token.'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal
      visible
      title={T('New integration token')}
      width={620}
      onCancel={onClose}
      footer={
        <Foot>
          <Note>{T('The token is shown once, right after creation.')}</Note>
          <CancelButton type="button" onClick={onClose}>
            {T('Cancel')}
          </CancelButton>
          <CreateButton type="button" onClick={create} disabled={!ready || busy}>
            {busy ? T('Creating...') : T('Create token')}
          </CreateButton>
        </Foot>
      }
    >
      <div>
        <Label htmlFor="integration-token-name">{T('Who is this integrator?')}</Label>
        <NameInput
          id="integration-token-name"
          value={name}
          invalid={false}
          autoFocus
          placeholder={TF('Billing ACME, BI dashboard, partner app...')}
          onChange={(event) => setName(event.target.value)}
        />
        <Hint>{T('This name is how you will recognize the token later, to revoke it.')}</Hint>

        <Block>
          <Label htmlFor="integration-token-contact">
            {T('Representative e-mail')} <Optional>{T('optional')}</Optional>
          </Label>
          <NameInput
            id="integration-token-contact"
            type="email"
            value={contactEmail}
            invalid={false}
            placeholder="ti@parceiro.com.br"
            onChange={(event) => setContactEmail(event.target.value)}
          />
          {/*
            Honestidade sobre o que acontece: o registro existe, o envio ainda nao. Prometer
            "avisamos a pessoa" seria mentira enquanto nao ha transporte de e-mail.
          */}
          <Hint>
            {T('Who to contact when the integration breaks. Recorded in the module history.')}{' '}
            <strong>{T('The token is never sent by e-mail.')}</strong>
          </Hint>
        </Block>

        <Block>
          <Label>{T('What can this token do?')}</Label>

          {INTEGRATION_SCOPES.map((scope) => {
            const marked = scopes.includes(scope);

            return (
              <ScopeRow key={scope} marked={marked} onClick={() => toggle(scope)}>
                <Mark marked={marked}>{marked ? '✓' : ''}</Mark>
                <div>
                  <ScopeName>
                    {scopeLabel(scope)}
                    {isSensitiveScope(scope) && (
                      <Sensitive>
                        <WarningOutlined />
                        {T('sensitive')}
                      </Sensitive>
                    )}
                  </ScopeName>
                  <ScopeHelp>{scopeHelp(scope)}</ScopeHelp>
                </div>
              </ScopeRow>
            );
          })}

          <Hint>{T('Nothing is granted by default: mark only what the integrator needs.')}</Hint>
        </Block>

        <Block>
          <Label>{T('Validity')}</Label>
          <DatePicker
            showTime
            format="DD/MM/YYYY HH:mm"
            value={expiresAt}
            placeholder={TF('Does not expire')}
            disabledDate={(current) => !!current && current < moment().startOf('day')}
            onChange={(value) => setExpiresAt(value)}
          />
          <Hint>{T('Leave empty for a token that does not expire.')}</Hint>
        </Block>

        {error && <Error>{error}</Error>}
      </div>
    </Modal>
  );
};
